import React, { useState, useEffect } from 'react'
import { Typography, Spin, Table, Card, Button, Modal, Input } from 'antd'
import WarningsFilter from './WarningsFilter'
import useDevices from '../hooks/useDevices'
import useElements from '../hooks/useElements'
import { apiFetch } from "../utils/api"
import { formatBrowser } from '../utils/dateUtils'
import dayjs from 'dayjs'
import { useGlobalLoading } from '../context/LoadingContext';

const { Title, Paragraph } = Typography

export default function WarningsView() {
  const { devices, loading: loadingDevices } = useDevices()
  const { elements, loading: loadingElements } = useElements()

  const [selectedDevices, setSelectedDevices] = useState([])
  const [selectedElements, setSelectedElements] = useState([])
  const [dateRange, setDateRange] = useState(null)
  const [warnings, setWarnings] = useState([])
  const { startLoading, stopLoading } = useGlobalLoading();
  const [statusFilter, setStatusFilter] = useState(['UNCONFIRMED'])
  const [selectedWarnings, setSelectedWarnings] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmComment, setConfirmComment] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!devices || devices.length === 0) return;
    if (!elements || elements.length === 0) return;

    const range = {
      from: dayjs().subtract(24, 'hour'),
      to: dayjs(),
    };

    handleApplyFilters(
      devices.map(d => d.id),
      elements.map(e => e.id),
      range,
      statusFilter
    );

  }, [devices, elements]);
  
  const handleApplyFilters = (deviceIds, elementIds, range, status) => {
    setPage(1);
    setSelectedDevices(deviceIds)
    setSelectedElements(elementIds)
    setDateRange(range)

    if (!deviceIds.length || !elementIds.length || !range) return

    startLoading()
    apiFetch('/api/warnings/filter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        deviceIds,
        from: range.from.toISOString(),
        to: range.to.toISOString(),
        elementIds,
        status
      }),
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then(setWarnings)
      .catch(err => {
        console.error('Failed to load warnings', err)
        setWarnings([])
      })
      .finally(() => stopLoading())
  }

  function escapeCSVField(field) {
    if (field === null || field === undefined) return ''
    const str = String(field)
    if (str.includes('"')) return `"${str.replace(/"/g, '""')}"`
    if (str.includes(',') || str.includes('\n')) return `"${str}"`
    return str
  }

  const formatThresholdValue = (w) => {
    if (!w.thresholdValue) return '';

    const v = w.thresholdValue;
    const t = w.thresholdType;

    if (t === 'MAX') return `> ${v}`;
    if (t === 'MIN') return `< ${v}`;
    return v;
  };

  const exportToCSV = () => {
    const headers = ['Status', 'Device', 'Element', 'Unit', 'Threshold Value', 'Created Date', 'Closed Date', 'Confirmed by', 'Confirmed At', 'Comment']
    const rows = warnings.map(w => [
      w.status,
      w.deviceName,
      w.elementName,
      w.unit,
      // w.thresholdType,
      // w.thresholdValue,
      formatThresholdValue(w),
      // w.measuredValue,
      formatBrowser(w.createdAt, { seconds: true }),
      formatBrowser(w.closedAt, { seconds: true }),
      w.confirmedBy,
      formatBrowser(w.confirmedAt, { seconds: true }),
      w.comment
    ])
    const csv = [headers, ...rows]
      .map(r => r.map(escapeCSVField).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', 'warnings.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleConfirmSelected = () => {
    if (selectedWarnings.length === 0) return;
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmation = async () => {
    if (!confirmComment.trim()) return;

    try {
      setConfirming(true);
      startLoading();

      const res = await apiFetch(`/api/warnings/confirm-batch`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          warningIds: selectedWarnings,
          comment: confirmComment.trim()
        })
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setConfirmModalOpen(false);
      setConfirmComment('');
      setSelectedWarnings([]);

      handleApplyFilters(selectedDevices, selectedElements, dateRange, statusFilter);

    } catch (err) {
      console.error("Batch confirm failed", err);
    } finally {
      setConfirming(false);
      stopLoading();
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedWarnings,
    onChange: (keys) => setSelectedWarnings(keys),
    getCheckboxProps: (record) => ({
      disabled: record.status === 'CONFIRMED',
    }),
  };

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <Title level={3}>Warnings</Title>

      <WarningsFilter
        devices={devices}
        elements={elements}
        initialDevices={selectedDevices}
        initialElements={selectedElements}
        initialRange={dateRange}
        onApplyFilters={handleApplyFilters}
        disabledDevices={loadingDevices}
        disabledElements={loadingElements}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      {warnings.length === 0 ? (
        <Paragraph style={{ marginTop: 40 }}>No warnings found for the selected filters.</Paragraph>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Card style={{ marginBottom: 24 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 16
            }}>
              <Button
                type="primary"
                disabled={selectedWarnings.length === 0}
                onClick={handleConfirmSelected}
              >
                Confirm Selected
              </Button>

              <Button onClick={exportToCSV} type="primary">
                Export CSV
              </Button>
            </div>
            <Table
              // dataSource={warnings.map((w, i) => ({ ...w, key: i }))}
              dataSource={warnings.map(w => ({ ...w, key: w.id }))}
              rowSelection={rowSelection}
              columns={[
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status) => {
                    const STATUS_COLOR = {
                      OPEN: '#faad14',
                      CLOSED: '#52c41a',
                      CONFIRMED: '#aaa'
                    }

                    return (
                      <span style={{ color: STATUS_COLOR[status] || 'black' }}>
                        {status}
                      </span>
                    )
                  }
                },
                { title: 'Device', dataIndex: 'deviceName', key: 'deviceName' },
                { title: 'Element', dataIndex: 'elementName', key: 'elementName' },
                { title: 'Unit', dataIndex: 'unit', key: 'unit' },
                // { title: 'Threshold Type', dataIndex: 'thresholdType', key: 'thresholdType' },
                // { title: 'Threshold Value', dataIndex: 'thresholdValue', key: 'thresholdValue' },
                {
                  title: 'Threshold',
                  key: 'thresholdValue',
                  render: (_, record) => formatThresholdValue(record)
                },
                // { title: 'Measured Value', dataIndex: 'measuredValue', key: 'measuredValue' },
                {
                  title: 'Created At',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: text => formatBrowser(text, { seconds: true })
                },
                {
                  title: 'Closed At',
                  dataIndex: 'closedAt',
                  key: 'closedAt',
                  render: text => text ? formatBrowser(text, { seconds: true }) : '-'
                },
                {
                  title: 'Confirmed by',
                  dataIndex: 'confirmedBy',
                  key: 'confirmedBy',
                  render: v => v || '-'
                },
                {
                  title: 'Confirmed At',
                  dataIndex: 'confirmedAt',
                  key: 'confirmedAt',
                  render: text => text ? formatBrowser(text, { seconds: true }) : '-'
                },
                {
                  title: 'Comment',
                  dataIndex: 'comment',
                  key: 'comment',
                  render: v => v || '-'
                }
              ]}
              size="small"
              pagination={{
                current: page,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (p) => setPage(p),
                onShowSizeChange: (_, size) => {
                  setPageSize(size);
                  setPage(1);
                }
              }}
            />
          </Card>
        </div>
      )}
      <Modal
        title="Confirm Warnings"
        open={confirmModalOpen}
        onCancel={() => {
          setConfirmModalOpen(false);
          setConfirmComment('');
        }}
        onOk={handleSubmitConfirmation}
        okText="Confirm"
        confirmLoading={confirming}
        okButtonProps={{ disabled: !confirmComment.trim() }}
      >
        <p>
          You are about to confirm {selectedWarnings.length} warnings.
        </p>

        <Typography.Text strong>
          Please provide a confirmation comment:
        </Typography.Text>

        <Input.TextArea
          rows={4}
          value={confirmComment}
          onChange={(e) => setConfirmComment(e.target.value)}
          placeholder="Write your confirmation comment..."
          style={{ marginTop: 8 }}
        />
      </Modal>
    </div>
  )
}
